<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.addCoupon") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #e1423d">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs" novalidate>
        <div class="row">
          <!-- Start:: Name Input -->
          <base-input
            col="12"
            type="text"
            :placeholder="$t('PLACEHOLDERS.couponName')"
            v-model.trim="data.name"
            :maxlength="100"
            required
          />
          <!-- End:: Name Input -->

          <!-- Start:: Code Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.usageCode')"
            v-model.trim="data.code"
            :maxlength="50"
            required
          />
          <!-- End:: Code Input -->

          <!-- Start:: Discount Percentage Input -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.discountPercentage')"
            v-model.number="data.discount_percentage"
            required
          />
          <!-- End:: Discount Percentage Input -->

          <!-- Start:: Start Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :disabledDate="disabledStartDate"
            :placeholder="$t('PLACEHOLDERS.startDate')"
            v-model.trim="data.starts_at"
            required
          />
          <!-- End:: Start Date Input -->

          <!-- Start:: End Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :disabledDate="disabledEndDate"
            :placeholder="$t('PLACEHOLDERS.endDate')"
            v-model.trim="data.ends_at"
            required
          />
          <!-- End:: End Date Input -->

          <!-- Start:: Status Input -->
          <base-select-input
            col="6"
            :optionsList="statusOptions"
            :placeholder="$t('PLACEHOLDERS.status')"
            v-model="data.is_active"
            required
          />
          <!-- End:: Status Input -->

          <!-- Start:: Users Multiselect Input -->
          <base-select-input
            col="6"
            :optionsList="usersList"
            :placeholder="$t('PLACEHOLDERS.users')"
            v-model="data.user_ids"
            multiple
          />
          <!-- End:: Users Multiselect Input -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "CreateCoupon",

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        name: null,
        code: null,
        discount_percentage: null,
        starts_at: null,
        ends_at: null,
        is_active: null,
        user_ids: [],
      },
      // End:: Data Collection To Send

      usersList: [],
    };
  },

  computed: {
    statusOptions() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.active"),
          value: true,
        },
        {
          id: 2,
          name: this.$t("STATUS.notActive"),
          value: false,
        },
      ];
    },

    // Disable dates before today for start date
    disabledStartDate() {
      return (current) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return current && current < today;
      };
    },

    // Disable dates before start date for end date
    disabledEndDate() {
      return (current) => {
        if (!this.data.starts_at) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return current && current < today;
        }
        const startDate = new Date(this.data.starts_at);
        startDate.setHours(0, 0, 0, 0);
        return current && current < startDate;
      };
    },
  },

  methods: {
    // Start:: Get Users
    async getUsers() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "users?filter[account_type]=client&filter[status]=active&paginate=false",
        });
        this.usersList = res.data.data?.data || res.data.data || [];
      } catch (error) {
        console.log(error.response?.data?.message);
        this.usersList = [];
      }
    },
    // End:: Get Users

    // Start:: Validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;

      // Validate name
      if (!this.data.name || this.data.name.trim().length < 2) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.couponNameMinLength") ||
            "Coupon name must be at least 2 characters"
        );
        return;
      }

      if (this.data.name.length > 100) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.couponNameMaxLength") ||
            "Coupon name must not exceed 100 characters"
        );
        return;
      }

      // Validate code
      if (!this.data.code || this.data.code.trim().length < 2) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.usageCodeMinLength") ||
            "Usage code must be at least 2 characters"
        );
        return;
      }

      if (this.data.code.length > 50) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.usageCodeMaxLength") ||
            "Usage code must not exceed 50 characters"
        );
        return;
      }

      // Validate discount percentage
      if (
        this.data.discount_percentage === null ||
        this.data.discount_percentage === undefined ||
        this.data.discount_percentage < 0 ||
        this.data.discount_percentage > 100
      ) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.discountPercentageRange") ||
            "Discount percentage must be between 0 and 100"
        );
        return;
      }

      // Validate dates
      if (!this.data.starts_at) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.startDateRequired") || "Start date is required"
        );
        return;
      }

      if (!this.data.ends_at) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.endDateRequired") || "End date is required"
        );
        return;
      }

      const startDate = new Date(this.data.starts_at);
      const endDate = new Date(this.data.ends_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.startDateMustBeTodayOrLater") ||
            "Start date must be today or later"
        );
        return;
      }

      if (endDate < startDate) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.endDateMustBeAfterStartDate") ||
            "End date must be equal to or after start date"
        );
        return;
      }

      // Validate status
      if (this.data.is_active === null || this.data.is_active === undefined) {
        this.isWaitingRequest = false;
        this.$message.error(
          this.$t("VALIDATION.statusRequired") || "Status is required"
        );
        return;
      }

      this.submitForm();
    },
    // End:: Validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      try {
        const requestData = {
          name: this.data.name.trim(),
          code: this.data.code.trim(),
          discount_percentage: parseInt(this.data.discount_percentage),
          starts_at: moment(this.data.starts_at).format("YYYY-MM-DD"),
          ends_at: moment(this.data.ends_at).format("YYYY-MM-DD"),
          // max_usage: 100,
          is_active:
            this.data.is_active.value !== undefined
              ? this.data.is_active.value
              : this.data.is_active,
          user_ids: Array.isArray(this.data.user_ids)
            ? this.data.user_ids.map((user) =>
                typeof user === "object" ? user.id : user
              )
            : [],
        };

        await this.$axios({
          method: "POST",
          url: "coupons",
          data: requestData,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push({ path: "/coupons/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Submit Form
  },

  created() {
    // Start:: Fire Methods
    this.getUsers();
    // End:: Fire Methods
  },
};
</script>
